import React, { useState } from 'react';
import { useBranches } from '../../db/hooks/useLookupData';
import { useDatabase } from '../../db/DatabaseProvider';
import { generateId, now } from '../../lib/uuid';
import { useHasPermission } from '../auth/RequirePermission';
import { useAuthStore } from '../../stores/auth.store';
import { useToast } from '../common/ToastProvider';
import { ConfirmDialog } from '../common/ConfirmDialog';
import { Pencil, Trash2, Building2, Plus } from 'lucide-react';
import { Drawer } from '../common/Drawer';
import { Button } from '../common/Button';

export function BranchManager() {
  const profile = useAuthStore((s) => s.profile);
  const user = useAuthStore((s) => s.user);
  const currentUserId = profile?.id || user?.id || 'system';
  const { branches, isLoading } = useBranches();
  const db = useDatabase();
  const hasPermission = useHasPermission();
  const canManageConfig = hasPermission('settings:manage');
  const toast = useToast();

  const [confirmModal, setConfirmModal] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    onConfirm: () => Promise<void>;
  }>({
    isOpen: false,
    title: '',
    message: '',
    onConfirm: async () => {},
  });

  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);

  const [name, setName] = useState('');
  const [code, setCode] = useState('');
  const [isActive, setIsActive] = useState(true);

  const openNew = () => {
    setEditingId(null);
    setName('');
    setCode('');
    setIsActive(true);
    setIsFormOpen(true);
  };

  const openEdit = (branch: any) => {
    setEditingId(branch.id);
    setName(branch.name);
    setCode(branch.code);
    setIsActive(branch.is_active !== false);
    setIsFormOpen(true);
  };

  const handleDelete = async (id: string) => {
    if (!db || !canManageConfig) return;

    const activeBranches = branches.filter(s => !s.is_deleted);
    if (activeBranches.length <= 1) {
      toast.warning('No se puede eliminar la única sucursal activa del sistema.', 'Acción Restringida');
      return;
    }

    const profiles = await db.user_profiles.find().exec();
    const userCount = profiles.filter(p => p.assigned_branches?.includes(id)).length;
    if (userCount > 0) {
      toast.warning(`No se puede eliminar esta sucursal porque está asignada a ${userCount} usuario(s).`, 'Asociación Activa');
      return;
    }

    const clientCount = await db.customers.count({ selector: { branch_id: id, is_deleted: { $ne: true } } }).exec();
    if (clientCount > 0) {
      toast.warning(`No se puede eliminar esta sucursal porque tiene ${clientCount} cliente(s) asociado(s).`, 'Asociación Activa');
      return;
    }

    const staffCount = await db.staff.count({ selector: { default_branch_id: id, is_deleted: { $ne: true } } }).exec();
    if (staffCount > 0) {
      toast.warning(`No se puede eliminar esta sucursal porque tiene ${staffCount} miembro(s) de staff asociado(s).`, 'Asociación Activa');
      return;
    }

    const transactionCount = await db.transactions.count({ selector: { branch_id: id, is_deleted: { $ne: true } } }).exec();
    if (transactionCount > 0) {
      toast.warning(`No se puede eliminar esta sucursal porque contiene ${transactionCount} transaccion(es) registrada(s).`, 'Asociación Activa');
      return;
    }

    setConfirmModal({
      isOpen: true,
      title: 'Eliminar Sucursal',
      message: '¿Estás seguro de que deseas eliminar esta sede del sistema?',
      onConfirm: async () => {
        try {
          const doc = await db.branches.findOne(id).exec();
          if (doc) {
            await doc.patch({
              is_deleted: true,
              updated_at: now(),
              
            });
          }
          toast.success('Sucursal eliminada correctamente');
        } catch (err: any) {
          toast.error('Error al eliminar sucursal: ' + err.message);
        } finally {
          setConfirmModal(prev => ({ ...prev, isOpen: false }));
        }
      }
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!db || !canManageConfig) return;

    const sanitizedName = name.trim();
    const sanitizedCode = code.trim().toUpperCase();

    if (!sanitizedName || !sanitizedCode) {
      toast.error('Por favor ingrese el name y el código de la sucursal.', 'Datos Incompletos');
      return;
    }

    const duplicateCode = branches.find(
      s => s.code.toUpperCase() === sanitizedCode && s.id !== editingId && !s.is_deleted
    );
    if (duplicateCode) {
      toast.error(`El código "${sanitizedCode}" ya está asignado a otra sucursal.`, 'Código Duplicado');
      return;
    }

    try {
      if (editingId) {
        const doc = await db.branches.findOne(editingId).exec();
        if (doc) {
          await doc.patch({
            name: sanitizedName,
            code: sanitizedCode,
            is_active: isActive,
            updated_at: now(),
            last_modified_by: currentUserId,
          });
        }
        toast.success('Sucursal actualizada correctamente');
      } else {
        const timestamp = now();
        await db.branches.insert({
          id: generateId(),
          name: sanitizedName,
          code: sanitizedCode,
          is_active: isActive,
          is_deleted: false,
          created_at: timestamp,
          updated_at: timestamp,
          last_modified_by: currentUserId,
          
        });
        toast.success('Sucursal guardada exitosamente');
      }
      setIsFormOpen(false);
    } catch (err: any) {
      toast.error('Error al guardar sucursal: ' + err.message);
    }
  };

  if (isLoading) return <div className="p-6 text-center text-xs text-text-tertiary">Cargando branches...</div>;

  return (
    <div className="border border-border bg-surface rounded-2xl shadow-xs overflow-hidden">
      {/* Harmonized Header Bar */}
      <div className="flex justify-between items-center px-6 py-4 border-b border-border bg-surface-secondary/40">
        <div>
          <h3 className="text-sm font-bold text-text-primary">Sucursales / Sedes</h3>
          <p className="text-xs text-text-secondary mt-0.5">Gestión de sedes operativas de la empresa</p>
        </div>
        {canManageConfig && (
          <Button variant="primary" size="sm" icon={Plus} onClick={openNew}>
            Nueva Sucursal
          </Button>
        )}
      </div>

      {/* Drawer Modal */}
      <Drawer
        isOpen={isFormOpen}
        onClose={() => setIsFormOpen(false)}
        title={editingId ? 'Editar Sucursal' : 'Nueva Sucursal'}
        subtitle="Ingresa el name descriptivo y código único de la sede"
        icon={Building2}
        maxWidth="md"
        footer={
          <>
            <Button variant="ghost" size="sm" onClick={() => setIsFormOpen(false)}>
              Cancelar
            </Button>
            <Button variant="primary" size="sm" type="submit" form="branch-form">
              Guardar Sucursal
            </Button>
          </>
        }
      >
        <form id="branch-form" onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-xs font-semibold text-text-secondary mb-1">Nombre de la Sede *</label>
            <input
              required
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full h-10 rounded-xl border border-border bg-surface px-3.5 text-sm text-text-primary focus:border-brand-sky focus:ring-1 focus:ring-brand-sky transition-all shadow-xs"
              placeholder="Ej. Sede Las Mercedes"
            />
          </div>

          <div>
            <label className="block text-xs font-semibold text-text-secondary mb-1">Código Único (Acrónimo) *</label>
            <input
              required
              type="text"
              value={code}
              onChange={(e) => setCodigo(e.target.value.toUpperCase())}
              className="w-full h-10 rounded-xl border border-border bg-surface px-3.5 text-sm text-text-primary focus:border-brand-sky focus:ring-1 focus:ring-brand-sky transition-all shadow-xs font-mono uppercase"
              placeholder="Ej. MER-01"
            />
          </div>

          <div className="flex items-center gap-3 pt-2">
            <input
              id="branch_active_chk"
              type="checkbox"
              checked={activa}
              onChange={(e) => setActiva(e.target.checked)}
              className="h-4 w-4 rounded border-border text-brand-orange focus:ring-brand-orange cursor-pointer"
            />
            <label htmlFor="branch_active_chk" className="text-xs font-bold text-text-primary cursor-pointer select-none">
              Sucursal Habilitada para Operaciones
            </label>
          </div>
        </form>
      </Drawer>

      {/* Table List */}
      <div>
        <table className="w-full text-left text-sm border-collapse">
          <thead className="bg-surface-secondary/60 text-xs uppercase text-text-tertiary border-b border-border">
            <tr>
              <th className="px-6 py-3.5 font-semibold">Nombre de la Sede</th>
              <th className="px-6 py-3.5 font-semibold">Código</th>
              <th className="px-6 py-3.5 font-semibold">Estado</th>
              <th className="px-6 py-3.5 font-semibold text-right">Acciones</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {branches.map((s) => (
              <tr key={s.id} className="hover:bg-surface-hover/30 transition-colors">
                <td className="px-6 py-3.5 font-bold text-text-primary">{s.name}</td>
                <td className="px-6 py-3.5 text-xs text-text-secondary font-mono">{s.code}</td>
                <td className="px-6 py-3.5">
                  <span className={`inline-flex px-2.5 py-0.5 rounded-full text-xs font-semibold ${s.is_active !== false ? 'bg-emerald-500/10 text-emerald-600 border border-emerald-500/20' : 'bg-slate-500/10 text-slate-400 border border-slate-500/20'}`}>
                    {s.is_active !== false ? 'Activa' : 'Inactiva'}
                  </span>
                </td>
                <td className="px-6 py-3.5 text-right">
                  {canManageConfig && (
                    <div className="flex justify-end gap-1">
                      <Button variant="ghost" size="xs" icon={Pencil} onClick={() => openEdit(s)} aria-label="Editar" />
                      <Button variant="danger" size="xs" icon={Trash2} onClick={() => handleDelete(s.id)} aria-label="Eliminar" />
                    </div>
                  )}
                </td>
              </tr>
            ))}
            {branches.length === 0 && (
              <tr>
                <td colSpan={4} className="px-6 py-8 text-center text-text-tertiary text-xs">No hay branches registradas</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <ConfirmDialog
        isOpen={confirmModal.isOpen}
        onClose={() => setConfirmModal(prev => ({ ...prev, isOpen: false }))}
        onConfirm={confirmModal.onConfirm}
        title={confirmModal.title}
        message={confirmModal.message}
        variant="danger"
        confirmText="Eliminar"
      />
    </div>
  );
}
